/**
 * Section 4 — Documentation + Complete + Reopen.
 *
 *  - C16 gate: Generate disabled while report.status !== 'ready'
 *  - A21: Generate downloads the DOCX synchronously
 *  - Complete sets reports.completed_at (90-day retention starts)
 *  - Reopen clears completed_at if still within the 90-day window
 *
 * 90-day retention per Sigon InfoSec Policy 3.2.1b.
 */
import { useState } from "react";
import {
  ExpandableSection,
  SpaceBetween,
  Box,
  Button,
  Alert,
  StatusIndicator,
  Modal,
  Input,
  FormField,
} from "@cloudscape-design/components";
import type { Report } from "@/core/api/types";
import { downloadWorkpaper, completeReport, reopenReport, triggerDownload } from "./api";

interface Props {
  report: Report;
  onUpdated: () => void;
}

function addDays(iso: string, days: number): string {
  const d = new Date(iso);
  d.setUTCDate(d.getUTCDate() + days);
  return d.toISOString().slice(0, 10);
}

export function DocsSection({ report, onUpdated }: Props) {
  const [busy, setBusy] = useState<"generate" | "complete" | "reopen" | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [confirmText, setConfirmText] = useState("");

  const ready = report.status === "ready";
  const completed = !!report.completed_at;
  // #121: a report can only be completed once a workpaper has been generated/filed.
  const workpaperFiled = !!report.workpaper_generated_at;
  const purgeOn = report.completed_at ? addDays(report.completed_at, 90) : null;

  const generate = async () => {
    setBusy("generate");
    setError(null);
    try {
      const blob = await downloadWorkpaper(report.id);
      const filename = `soc1-workpaper-${report.service_org_name || report.id}.docx`.replace(
        /[^A-Za-z0-9._-]+/g,
        "_"
      );
      triggerDownload(blob, filename);
      // #121: generating files the workpaper server-side — refresh so
      // workpaper_generated_at lands and "Mark report complete" appears.
      onUpdated();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(null);
    }
  };

  const doComplete = async () => {
    setBusy("complete");
    setError(null);
    try {
      await completeReport(report.id);
      setConfirmOpen(false);
      setConfirmText("");
      onUpdated();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(null);
    }
  };

  const doReopen = async () => {
    setBusy("reopen");
    setError(null);
    try {
      await reopenReport(report.id);
      onUpdated();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(null);
    }
  };

  const expectedConfirm = report.service_org_name || "";
  const confirmMatch = confirmText.trim() === expectedConfirm.trim() && expectedConfirm !== "";

  return (
    <ExpandableSection
      variant="container"
      defaultExpanded={false}
      headerText="Documentation"
      headerActions={
        completed && (
          <StatusIndicator type="success">Done {report.completed_at?.slice(0, 10)}</StatusIndicator>
        )
      }
    >
      <SpaceBetween size="m">
        {error && (
          <Alert type="error" header="Action failed">
            {error}
          </Alert>
        )}

        {!ready && !completed && (
          <Alert type="info" header="Ingest still running">
            The Generate button unlocks once the report is fully processed and Scope, Controls, and CUECs have
            AI verdicts. The view above auto-refreshes.
          </Alert>
        )}

        {completed && purgeOn && (
          <Alert type="info" header={`Retained until ${purgeOn}`}>
            This completed report and its evidence stay fully accessible until {purgeOn}, then are purged per
            Sigon InfoSec Policy 3.2.1b (90-day retention). Reopen below if you need to make changes.
          </Alert>
        )}

        <Box>
          <Box variant="awsui-key-label">A21 — Generate workpaper</Box>
          <Box color="text-body-secondary" variant="small">
            Renders the V3 DOCX template — cover, 6-bucket executive summary, scope, bridge letter (if
            applicable), exceptions, CUECs, and an empty sign-off block. Sign-off happens outside this app.
          </Box>
        </Box>

        <SpaceBetween direction="horizontal" size="xs">
          <Button
            variant="primary"
            disabled={!ready || busy !== null}
            loading={busy === "generate"}
            onClick={generate}
            iconName="download"
          >
            Generate workpaper (.docx)
          </Button>

          {/* #121: only after a workpaper has been generated/filed. */}
          {!completed && workpaperFiled && (
            <Button
              disabled={!ready || busy !== null}
              onClick={() => setConfirmOpen(true)}
              iconName="status-positive"
            >
              Mark report complete
            </Button>
          )}

          {completed && (
            <Button disabled={busy !== null} loading={busy === "reopen"} onClick={doReopen}>
              Reopen report
            </Button>
          )}
        </SpaceBetween>

        {!completed && !workpaperFiled && ready && (
          <Box color="text-body-secondary" variant="small">
            Generate the workpaper first — "Mark report complete" unlocks once it's filed.
          </Box>
        )}
      </SpaceBetween>

      <Modal
        visible={confirmOpen}
        onDismiss={() => {
          setConfirmOpen(false);
          setConfirmText("");
        }}
        header="Complete report"
        footer={
          <Box float="right">
            <SpaceBetween direction="horizontal" size="xs">
              <Button
                onClick={() => {
                  setConfirmOpen(false);
                  setConfirmText("");
                }}
                disabled={busy === "complete"}
              >
                Cancel
              </Button>
              <Button
                variant="primary"
                disabled={!confirmMatch || busy === "complete"}
                loading={busy === "complete"}
                onClick={doComplete}
              >
                Complete report
              </Button>
            </SpaceBetween>
          </Box>
        }
      >
        <SpaceBetween size="s">
          <Box>
            Completing the report locks all edits and starts the 90-day retention timer. The report stays
            visible and fully accessible in your Reports list throughout that window, then is purged. You can
            Reopen within the window if needed.
          </Box>
          <FormField label={`Type "${expectedConfirm}" to confirm`}>
            <Input value={confirmText} onChange={({ detail }) => setConfirmText(detail.value)} />
          </FormField>
        </SpaceBetween>
      </Modal>
    </ExpandableSection>
  );
}
